/* eslint-disable react-refresh/only-export-components */

// =================================================================
// RAÍZ CUADRADA — FLAG COMPONENTS
// SVG Flag Icons using country-flag-icons library
// =================================================================

import React from 'react';
import { cn } from '@/lib/utils';

// Import all flags from country-flag-icons (3x2 aspect ratio, optimized SVGs)
import * as Flags from 'country-flag-icons/react/3x2';

interface FlagIconProps {
  /** ISO 3166-1 alpha-2 country code (e.g., 'MX', 'US') */
  countryCode: string;
  /** Size in pixels (width, height is auto based on 3:2 ratio) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Fallback emoji if flag not found */
  fallbackEmoji?: string;
}

// Type for flag components from the library
type FlagComponentType = React.ComponentType<{ title?: string; className?: string; style?: React.CSSProperties }>;

/**
 * Renders an SVG flag icon for a given country code
 * Falls back to emoji if country not found
 */
export function FlagIcon({
  countryCode,
  size = 20,
  className,
  fallbackEmoji = '🏳️'
}: FlagIconProps) {
  const code = countryCode.toUpperCase();

  // Get the flag component dynamically
  const FlagComponent = (Flags as unknown as Record<string, FlagComponentType>)[code];

  if (FlagComponent) {
    return (
      <FlagComponent
        className={cn("inline-block rounded-sm shadow-sm", className)}
        style={{
          width: size,
          height: size * (2 / 3), // 3:2 aspect ratio
        }}
      />
    );
  }

  // Fallback to emoji
  return (
    <span
      className={cn("inline-flex items-center justify-center", className)}
      style={{ width: size, height: size * (2 / 3) }}
    >
      {fallbackEmoji}
    </span>
  );
}

/**
 * Get flag component by country code
 * Returns undefined if not found
 */
export function getFlagComponent(countryCode: string): FlagComponentType | undefined {
  const code = countryCode.toUpperCase();
  return (Flags as unknown as Record<string, FlagComponentType>)[code];
}

/**
 * Check if a flag exists for a country code
 */
export function hasFlag(countryCode: string): boolean {
  const code = countryCode.toUpperCase();
  return code in Flags;
}

// Re-export all flags for direct use
export { Flags };

// Common flag components for direct import
export const MX = Flags.MX;
export const US = Flags.US;
export const CA = Flags.CA;
export const ES = Flags.ES;
export const CO = Flags.CO;
export const AR = Flags.AR;
export const BR = Flags.BR;
export const CL = Flags.CL;
export const PE = Flags.PE;
export const VE = Flags.VE;
export const GT = Flags.GT;
export const SV = Flags.SV;
export const HN = Flags.HN;
export const NI = Flags.NI;
export const CR = Flags.CR;
export const PA = Flags.PA;
export const CU = Flags.CU;
export const DO = Flags.DO;
export const PR = Flags.PR;
export const EC = Flags.EC;
export const BO = Flags.BO;
export const PY = Flags.PY;
export const UY = Flags.UY;
export const GB = Flags.GB;
export const FR = Flags.FR;
export const DE = Flags.DE;
export const IT = Flags.IT;
export const CN = Flags.CN;
export const JP = Flags.JP;
export const KR = Flags.KR;
export const IN = Flags.IN;
export const AU = Flags.AU;
export const NZ = Flags.NZ;
export const ZA = Flags.ZA;
export const SA = Flags.SA;
export const AE = Flags.AE;